import { ViewBooking, ViewCustomer } from "collins-js-sdk";
import React, { memo, useState } from "react";
import { addOrUpdateBooking } from "../../../../../../APIs/Booking/UpdateBooking";
import { patchCustomer } from "../../../../../../APIs/Customer/updateCustomer";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { getCustomerName } from "../../../../../../utils/customer";
import { EDSButton } from "../../../../../eds/EDSButton";
import DetachedModal from "../../../../../Shared/Modals/DetachedModal/DetachedModal";
import { getAllBookingLabels, getAllCustomerLabels } from "../BookingLabels";
import ButtonList from "./ButtonList";
import { AssignedBookingLabelsType } from "./ManageLabels";

type ManageLabelsModalType = {
  selectedBooking: ViewBooking;
  selectedCustomer?: ViewCustomer;
  handleClose: () => void;
};

const ManageLabelsModal = ({
  selectedBooking,
  selectedCustomer,
  handleClose
}: ManageLabelsModalType) => {
  const { venueGroupConfig } = useAppContext();
  const diaryContext = useDiaryContext();

  const [assignedBookingLabels, setAssignedBookingLabels] =
    useState<AssignedBookingLabelsType>(selectedBooking.labels ?? []);
  const [assignedCustomerLabels, setAssignedCustomerLabels] =
    useState<AssignedBookingLabelsType>(selectedCustomer?.labels ?? []);

  if (!diaryContext.venueConfig || !venueGroupConfig) {
    return null;
  }

  const assignableBookingLabels = getAllBookingLabels({
    venueConfig: diaryContext.venueConfig,
    venueGroupConfig
  });
  const assignableCustomerLabels = getAllCustomerLabels({
    venueConfig: diaryContext.venueConfig,
    venueGroupConfig
  });

  return (
    <DetachedModal
      heading={`Manage Labels (${
        assignedBookingLabels.length + assignedCustomerLabels.length
      })`}
      width="70%"
      handleClose={handleClose}
    >
      <div className="ManageLabels h-full flex flex-col">
        <div className="ManageBookingLabels flex flex-col flex-[1_100%]">
          <div className="ManageLabelsSubHeading text-base mt-6 mb-3">
            Booking Labels
          </div>
          <div className="AssignableLabels flex flex-wrap gap-2 overflow-y-auto overflow-x-hidden flex-1 min-h-0 max-h-[400px] grow content-start">
            <ButtonList
              data={assignableBookingLabels}
              assignedLabels={selectedBooking.labels}
              onChange={setAssignedBookingLabels}
              noRecords="No booking labels found."
            />
          </div>
        </div>
        <div className="ManageCustomerLabels flex flex-col flex-[1_100%]">
          <div className="ManageLabelsSubHeading text-base mt-6 mb-3">
            {`${getCustomerName(selectedCustomer) || "Customer"} `}
            Labels
          </div>
          <div className="AssignableLabels flex flex-wrap gap-2 overflow-y-auto overflow-x-hidden flex-1 min-h-0 max-h-[400px] grow content-start">
            <ButtonList
              data={selectedCustomer?.id ? assignableCustomerLabels : []}
              assignedLabels={selectedCustomer?.labels}
              onChange={setAssignedCustomerLabels}
              noRecords={`${
                selectedCustomer?.id
                  ? "No customer labels found."
                  : "Please add the customers email address to this booking in order to manage customer labels."
              }`}
            />
          </div>
        </div>

        <div className="ActionButtons flex justify-end items-center pt-panel-padding gap-3">
          <EDSButton onClick={handleClose} variant="secondary" size="md">
            Cancel
          </EDSButton>
          <EDSButton
            onClick={() => {
              selectedBooking.labels = assignedBookingLabels;
              addOrUpdateBooking({
                booking: selectedBooking,
                diaryContext
              });

              if (selectedCustomer?.id) {
                selectedCustomer.labels = assignedCustomerLabels;
                patchCustomer(selectedCustomer, {
                  labels: selectedCustomer.labels
                });
              }

              handleClose();
            }}
            variant="default"
            size="md"
            className="SaveButton"
          >
            Save
          </EDSButton>
        </div>
      </div>
    </DetachedModal>
  );
};

export default memo<ManageLabelsModalType>(ManageLabelsModal);
